import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { OnboardingBackButton } from '../../components/OnboardingBackButton';
import { clearPendingPartnerIntent } from '../../utils/partnerIntent';

export default function VenueOnboardingPending() {
  const navigate = useNavigate();

  // Landing here means the venue application was saved past the fresh
  // signup step — wipe the partner intent so later cold-launches don't
  // keep re-routing to /venue/onboarding/1.
  React.useEffect(() => { clearPendingPartnerIntent(); }, []);

  const handleLogoutAndGoLogin = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell premium-page"
      style={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
        paddingLeft: 28,
        paddingRight: 28,
      }}
    >
      {/* Back */}
      <div
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 20px)',
          left: 20,
        }}
      >
        <OnboardingBackButton
          onClick={handleLogoutAndGoLogin}
          testId="venue-pending-back"
          ariaLabel="Go to login"
        />
      </div>

      {/* Logo */}
      <img
        src="/allure-logo.png"
        alt="Allure"
        style={{
          width: 120,
          height: 107,
          objectFit: 'contain',
          marginBottom: 40,
        }}
      />

      {/* Clock icon */}
      <motion.div
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: 'var(--app-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 32,
        }}
      >
        <Clock size={40} color="var(--accent-purple)" />
      </motion.div>

      <h2
        style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 26,
          fontWeight: 700,
          color: 'var(--text)',
          textAlign: 'center',
          marginBottom: 16,
        }}
      >
        Application in Progress
      </h2>

      <p
        style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 18,
          fontWeight: 400,
          color: 'var(--text-2)',
          textAlign: 'center',
          lineHeight: 1.5,
          maxWidth: 300,
          marginBottom: 16,
        }}
      >
        Your venue application has been saved. We will provide you with the remaining steps soon.
      </p>

      <p
        style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 16,
          fontWeight: 400,
          color: 'var(--text-3)',
          textAlign: 'center',
          lineHeight: 1.5,
          maxWidth: 280,
        }}
      >
        Steps 4-7 are coming soon. You will be notified when they are available.
      </p>

      <div style={{ height: 60 }} />

      {/* Back to login */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleLogoutAndGoLogin}
        data-testid="venue-pending-login-btn"
        className="premium-pill-button"
        style={{
          width: 220,
          padding: '14px 0',
          fontFamily: "'Inria Serif', serif",
          fontSize: 18,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Back to Login
      </motion.button>
    </motion.div>
  );
}
